import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  selectProviderSessions,
  selectProviderSessionsPagination,
  selectProviderSessionsStatus,
} from 'features/Provider/providerSelectors';
import {
  getProviderSessions,
  providerActions,
} from 'features/Provider/providerSlice';
import {selectUserProfile} from 'features/User';
import {GetSessionsFilters, SliceStatus} from 'interfaces';
import {useDispatch, useSelector} from 'react-redux';

const BILLING_SESSION_SIZE = 10;

interface UseRevenueSessionsProps {
  isSingleSession: boolean;
}

export function useRevenueSessions({isSingleSession}: UseRevenueSessionsProps) {
  const dispatch = useDispatch();
  const sessions = useSelector(selectProviderSessions);
  const billingSessionsStatus = useSelector(selectProviderSessionsStatus);
  const {hasNextPage, nextPage} = useSelector(selectProviderSessionsPagination);
  const user = useSelector(selectUserProfile);

  const [filters, setFilters] = useState<GetSessionsFilters>({
    memberType: '',
    paymentType: '',
    insuranceCompany: [],
    dateRange: {from: null, to: null},
    sortBy: 'latest',
    search: '',
    providerId: user?._id ?? '',
    sessionType: isSingleSession ? 'individual' : 'group',
  });

  const hasInitiallyLoadedRef = useRef(false);

  const handleFilterChange = useCallback((newFilters: GetSessionsFilters) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters,
    }));
  }, []);

  const loadMoreData = useCallback(() => {
    if (hasNextPage && billingSessionsStatus !== SliceStatus.pending) {
      dispatch(
        getProviderSessions({
          pageNo: nextPage,
          size: BILLING_SESSION_SIZE,
          ...filters,
        }),
      );
    }
  }, [hasNextPage, billingSessionsStatus, dispatch, nextPage, filters]);

  useEffect(() => {
    if (!hasInitiallyLoadedRef.current) {
      hasInitiallyLoadedRef.current = true;
    }

    dispatch(providerActions.resetProviderSessions());
    dispatch(
      getProviderSessions({
        pageNo: 1,
        size: BILLING_SESSION_SIZE,
        ...filters,
      }),
    );
  }, [
    filters.memberType,
    filters.paymentType,
    filters.insuranceCompany,
    filters.dateRange,
    filters.search,
    filters.sortBy,
    dispatch,
  ]);

  useEffect(() => {
    return () => {
      dispatch(providerActions.resetProviderSessions());
    };
  }, [dispatch]);

  const memoizedSessions = useMemo(() => sessions, [sessions]);

  useEffect(() => {
    dispatch(providerActions.setMemoizedSessions(memoizedSessions));
  }, [dispatch, memoizedSessions]);

  const isLoading = billingSessionsStatus === SliceStatus.pending;
  const hasNoSessions =
    sessions.length === 0 && billingSessionsStatus === SliceStatus.resolved;

  return {
    sessions: memoizedSessions,
    filters,
    hasNextPage,
    isLoading,
    hasNoSessions,
    billingSessionsStatus,
    handleFilterChange,
    loadMoreData,
  };
}
